import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import ConfirmModal from "../components/ConfirmModal";
import Pagination from "../components/Pagination";

import winnerService from "../services/winnerService";

const ITEMS_PER_PAGE = 8;
const AUTO_REFRESH_INTERVAL = 15000;

const PendingWinners = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [winners, setWinners] = useState([]);
  const [paidClaims, setPaidClaims] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [selectedBet, setSelectedBet] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [paying, setPaying] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const formatAmount = (value) => {
    return Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-IN");
  };

  const fetchPendingWinners = useCallback(
    async ({ initialLoad = false, manualRefresh = false } = {}) => {
      try {
        if (initialLoad) setLoading(true);
        if (manualRefresh) setRefreshing(true);

        const response = await winnerService.getPendingWinners();

        if (!response?.success) {
          if (initialLoad || manualRefresh) {
            toast.error(
              response?.message || "Unable to load pending winners."
            );
          }
          return;
        }

        const rows = Array.isArray(response.winners) ? response.winners : [];
        setWinners(rows);

        if (manualRefresh) {
          toast.success("Pending winners updated successfully.");
        }
      } catch (error) {
        console.error("Pending Winners Error:", error);
        if (initialLoad || manualRefresh) {
          toast.error(
            error.response?.data?.message || "Unable to load pending winners."
          );
        }
      } finally {
        if (initialLoad) setLoading(false);
        if (manualRefresh) setRefreshing(false);
      }
    },
    []
  );

  const fetchPaidWinners = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await winnerService.getPaidWinners();

      if (!response?.success) {
        if (!silent) {
          toast.error(
            response?.message || "Unable to load paid claim history."
          );
        }
        return;
      }

      setPaidClaims(Array.isArray(response.claims) ? response.claims : []);
    } catch (error) {
      console.error("Paid Winners Error:", error);
      if (!silent) {
        toast.error(
          error.response?.data?.message || "Unable to load paid claim history."
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchPendingWinners({ initialLoad: true });
    fetchPaidWinners({ silent: true });
  }, [fetchPendingWinners, fetchPaidWinners]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const intervalId = window.setInterval(() => {
      fetchPendingWinners();
      fetchPaidWinners({ silent: true });
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [autoRefresh, fetchPendingWinners, fetchPaidWinners]);

  const totalPages = Math.max(
    1,
    Math.ceil(winners.length / ITEMS_PER_PAGE)
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentWinners = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return winners.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [winners, currentPage]);

  const totalPendingAmount = useMemo(() => {
    return winners.reduce(
      (total, winner) => total + Number(winner.win_amount || 0),
      0
    );
  }, [winners]);

  const totalPaidAmount = useMemo(() => {
    return paidClaims.reduce(
      (total, claim) => total + Number(claim.win_amount || 0),
      0
    );
  }, [paidClaims]);

  const totalBetAmount = useMemo(() => {
    return winners.reduce(
      (total, winner) => total + Number(winner.amount || 0),
      0
    );
  }, [winners]);

  const handleOpenPayModal = (winner) => {
    if (
      String(winner?.claim_status || "PENDING").toUpperCase() !== "PENDING"
    ) {
      toast.error("This winning claim is not pending.");
      return;
    }
    setSelectedBet(winner);
    setShowConfirmModal(true);
  };

  const handleClosePayModal = () => {
    if (paying) return;
    setShowConfirmModal(false);
    setSelectedBet(null);
  };

  const handleMarkPaid = async () => {
    const betId = Number(selectedBet?.id);

    if (!Number.isInteger(betId) || betId <= 0) {
      toast.error("Invalid winning bet.");
      return;
    }

    try {
      setPaying(true);

      const response = await winnerService.markWinningPaid(betId);

      if (!response?.success) {
        toast.error(
          response?.message || "Unable to mark winning as paid."
        );
        return;
      }

      setWinners((previous) =>
        previous.filter((winner) => Number(winner.id) !== betId)
      );

      toast.success(
        response.message || "Winning payout marked as paid."
      );

      setShowConfirmModal(false);
      setSelectedBet(null);

      await Promise.all([
        fetchPendingWinners(),
        fetchPaidWinners({ silent: true }),
      ]);
    } catch (error) {
      console.error("Mark Winner Paid Error:", error);
      toast.error(
        error.response?.data?.message || "Unable to mark winning as paid."
      );
    } finally {
      setPaying(false);
    }
  };

  const startRecord =
    winners.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const endRecord = Math.min(currentPage * ITEMS_PER_PAGE, winners.length);

  return (
    <div className="d-flex flex-column min-vh-100 bg-[#070A12] text-[#F8FAFC]">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="flex-grow-1 p-3 p-md-4 overflow-auto bg-[#070A12]">
          <div className="container-fluid">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4 pb-3 border-b border-[rgba(148,163,184,0.14)]">
              <div>
                <h2 className="fw-bold text-[#F8FAFC] mb-1">
                  Pending Winners
                </h2>

                <p className="text-[#94A3B8] mb-0">
                  Verify each winning bet, confirm the offline cash payout and mark the claim as paid.
                </p>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="form-check form-switch mb-0">
                  <input
                    id="autoRefreshSwitch"
                    className="form-check-input"
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(event) =>
                      setAutoRefresh(event.target.checked)
                    }
                    disabled={paying}
                  />

                  <label
                    className="form-check-label fw-semibold small text-[#CBD5E1]"
                    htmlFor="autoRefreshSwitch"
                  >
                    Auto Refresh (15s)
                  </label>
                </div>

                <button
                  type="button"
                  className="flex items-center gap-2 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-2 text-xs font-bold text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#070A12] transition"
                  onClick={async () => {
                    await Promise.all([
                      fetchPendingWinners({ manualRefresh: true }),
                      fetchPaidWinners({ silent: true }),
                    ]);
                  }}
                  disabled={loading || refreshing || paying}
                >
                  {refreshing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-1" />
                      Refresh
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* DESKTOP METRICS (SHOWN ON LAPTOP/DESKTOP) */}
            <div className="hidden sm:flex row g-3 mb-4">
              <div className="col-12 col-md-4">
                <div className="rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-4 text-[#F8FAFC] shadow-lg h-100 d-flex align-items-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400 me-3">
                    <i className="bi bi-hourglass-split fs-4" />
                  </div>

                  <div>
                    <div className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider">
                      Pending Claims
                    </div>

                    <h3 className="fw-bold text-[#F8FAFC] mb-0 mt-0.5">
                      {winners.length}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-4 text-[#F8FAFC] shadow-lg h-100 d-flex align-items-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-blue-500/30 bg-blue-500/10 text-cyan-400 me-3">
                    <i className="bi bi-coin fs-4" />
                  </div>

                  <div>
                    <div className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider">
                      Total Bet Amount
                    </div>

                    <h5 className="fw-bold text-[#F8FAFC] mb-0 mt-0.5">
                      🪙 {formatAmount(totalBetAmount)}
                    </h5>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-4 text-[#F8FAFC] shadow-lg h-100 d-flex align-items-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 me-3">
                    <i className="bi bi-trophy-fill fs-4" />
                  </div>

                  <div>
                    <div className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider">
                      Pending Winning Amount
                    </div>

                    <h5 className="fw-bold text-emerald-400 mb-0 mt-0.5">
                      🪙 {formatAmount(totalPendingAmount)}
                    </h5>
                  </div>
                </div>
              </div>
            </div>

            {/* MOBILE COMPACT METRICS GRID (SHOWN ONLY ON MOBILE) */}
            <div className="grid sm:hidden grid-cols-3 gap-2 mb-4">
              <div className="rounded-xl border border-amber-500/30 bg-[#121827] p-2.5 text-center shadow-md">
                <span className="text-[10px] font-extrabold text-amber-400 uppercase tracking-wider block">Pending</span>
                <span className="text-sm font-black text-[#F8FAFC]">{winners.length}</span>
              </div>
              <div className="rounded-xl border border-blue-500/30 bg-[#121827] p-2.5 text-center shadow-md">
                <span className="text-[10px] font-extrabold text-cyan-400 uppercase tracking-wider block">Total Bet</span>
                <span className="text-xs font-black text-[#F8FAFC]">🪙 {formatAmount(totalBetAmount)}</span>
              </div>
              <div className="rounded-xl border border-emerald-500/30 bg-[#121827] p-2.5 text-center shadow-md">
                <span className="text-[10px] font-extrabold text-emerald-400 uppercase tracking-wider block">Winning</span>
                <span className="text-xs font-black text-emerald-400">🪙 {formatAmount(totalPendingAmount)}</span>
              </div>
            </div>

            <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3.5 text-xs text-blue-300 mb-4 flex items-center gap-2.5 shadow-lg">
              <i className="bi bi-info-circle-fill text-blue-400 text-base" />
              <span>Mark a claim as paid only after the computer terminal cash winning payout is completed.</span>
            </div>

            <div className="rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#121827] shadow-xl overflow-hidden mb-4">
              <div className="border-b border-[rgba(148,163,184,0.14)] bg-[#121827] p-4">
                <h5 className="fw-bold mb-1 text-[#F8FAFC]">
                  Pending Winning Claims
                </h5>

                <p className="small text-[#94A3B8] mb-0">
                  Computer Code, Round, Bet Type, Bet Value and Winning Number are shown for complete payout verification.
                </p>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <Loader message="Checking pending winners..." />
                ) : winners.length === 0 ? (
                  <div className="text-center py-5 px-3">
                    <i className="bi bi-trophy display-4 text-[#64748B]" />

                    <h5 className="fw-bold text-[#F8FAFC] mt-3">
                      No Pending Winner Claims
                    </h5>

                    <p className="text-[#94A3B8] mb-0">
                      All winning payouts have been processed.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* DESKTOP TABLE VIEW */}
                    <div className="hidden sm:block table-responsive">
                      <table className="table admin-table align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="ps-4">Bet ID</th>
                            <th>Computer Code</th>
                            <th>Round</th>
                            <th>Bet Type</th>
                            <th>Bet Value</th>
                            <th>Winning No.</th>
                            <th>Bet Amount</th>
                            <th>Winning Amount</th>
                            <th>Status</th>
                            <th>Date / Time</th>
                            <th className="text-end pe-4">Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {currentWinners.map((winner) => (
                            <tr key={winner.id}>
                              <td className="ps-4 fw-bold text-[#94A3B8]">
                                #{winner.id}
                              </td>

                              <td>
                                <div className="fw-bold text-[#D4AF37]">
                                  💻 {winner.computer_code || winner.username || "N/A"}
                                </div>
                              </td>

                              <td>
                                <span className="rounded-md border border-[rgba(148,163,184,0.14)] bg-[#0D111C] px-2 py-0.5 text-xs text-[#94A3B8]">
                                  Round #{winner.round_no || "N/A"}
                                </span>
                              </td>

                              <td>
                                <span className="rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-2 py-0.5 text-xs font-bold text-[#D4AF37] uppercase">
                                  {winner.bet_type || "N/A"}
                                </span>
                              </td>

                              <td>
                                <span className="fw-bold text-[#F8FAFC]">
                                  {winner.bet_value ?? "N/A"}
                                </span>
                              </td>

                              <td>
                                <span className="inline-flex items-center justify-center rounded-lg border border-emerald-500/30 bg-emerald-500/20 px-3 py-1 text-xs font-black text-emerald-400">
                                  🎯 {winner.winning_number ?? "N/A"}
                                </span>
                              </td>

                              <td className="fw-semibold text-[#CBD5E1]">
                                🪙 {formatAmount(winner.amount)}
                              </td>

                              <td className="fw-bold text-emerald-400">
                                🏆 🪙 {formatAmount(winner.win_amount)}
                              </td>

                              <td>
                                <span className="rounded-full border border-amber-500/30 bg-amber-500/20 px-3 py-1 text-[11px] font-bold text-amber-400">
                                  {winner.claim_status || "PENDING"}
                                </span>
                              </td>

                              <td className="small text-[#64748B]">
                                {formatDate(winner.created_at)}
                              </td>

                              <td className="text-end pe-4">
                                <button
                                  type="button"
                                  className="rounded-lg bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-500 hover:to-green-400 text-white font-bold px-3 py-1.5 text-xs shadow-md shadow-emerald-900/40 transition"
                                  onClick={() => handleOpenPayModal(winner)}
                                  disabled={paying}
                                >
                                  <i className="bi bi-check-circle-fill me-1" />
                                  Mark Paid
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* MOBILE CARDS VIEW */}
                    <div className="block sm:hidden p-3 space-y-3">
                      {currentWinners.map((winner) => (
                        <div
                          key={winner.id}
                          className="rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-3 shadow-lg"
                        >
                          <div className="flex items-center justify-between border-b border-[rgba(148,163,184,0.1)] pb-2">
                            <div className="flex items-center gap-2">
                              <span className="rounded-md border border-slate-700 bg-[#121827] px-2 py-0.5 text-xs font-mono font-bold text-slate-300">
                                #{winner.id}
                              </span>
                              <span className="fw-bold text-[#D4AF37] text-xs">
                                💻 {winner.computer_code || winner.username || "N/A"}
                              </span>
                            </div>
                            <span className="rounded-md border border-[rgba(148,163,184,0.14)] bg-[#121827] px-2 py-0.5 text-[10px] text-[#94A3B8]">
                              Round #{winner.round_no || "N/A"}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-[#121827] p-2 rounded-lg border border-[rgba(148,163,184,0.1)]">
                              <span className="text-[10px] text-[#94A3B8] block font-semibold">Bet Choice</span>
                              <span className="fw-bold text-[#F8FAFC] text-uppercase">
                                {winner.bet_type || "N/A"} ({winner.bet_value ?? "N/A"})
                              </span>
                            </div>
                            <div className="bg-emerald-500/10 border border-emerald-500/20 p-2 rounded-lg text-center">
                              <span className="text-[10px] text-emerald-400 block fw-semibold">Winning No</span>
                              <span className="inline-block bg-emerald-500 text-[#070A12] text-xs font-black px-2.5 py-0.5 rounded-md">
                                🎯 {winner.winning_number ?? "N/A"}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between bg-[#121827] p-2.5 rounded-xl text-xs border border-[rgba(148,163,184,0.1)]">
                            <div>
                              <span className="text-[10px] text-[#94A3B8] block">Bet Amount</span>
                              <span className="fw-semibold text-[#CBD5E1]">🪙 {formatAmount(winner.amount)}</span>
                            </div>
                            <div className="text-end">
                              <span className="text-[10px] text-[#94A3B8] block">Winning Payout</span>
                              <span className="fw-bold text-emerald-400 text-sm">🏆 🪙 {formatAmount(winner.win_amount)}</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-500 hover:to-green-400 text-white font-bold py-2.5 text-xs shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 transition"
                            onClick={() => handleOpenPayModal(winner)}
                            disabled={paying}
                          >
                            <i className="bi bi-check-circle-fill text-sm" />
                            Mark Paid (🪙 {formatAmount(winner.win_amount)})
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {!loading && winners.length > 0 && (
                <div className="border-t border-[rgba(148,163,184,0.14)] bg-[#121827] p-3">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div className="small text-[#94A3B8]">
                      Showing {startRecord}–{endRecord} of {winners.length} pending claims
                    </div>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ================= PAID CLAIM HISTORY ================= */}
            <div className="rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#121827] shadow-xl overflow-hidden mt-4">
              <div className="border-b border-[rgba(148,163,184,0.14)] bg-[#121827] p-4">
                <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                  <div>
                    <h5 className="fw-bold mb-1 text-[#F8FAFC]">
                      <i className="bi bi-check-circle-fill text-emerald-400 me-2" />
                      Paid Claims History
                    </h5>

                    <p className="small text-[#94A3B8] mb-0">
                      Completed winning payouts and Shop wallet credits.
                    </p>
                  </div>

                  <div className="text-md-end">
                    <div className="small text-[#94A3B8] fw-semibold">
                      Total Paid Claims
                    </div>

                    <div className="fw-bold text-[#F8FAFC]">{paidClaims.length}</div>

                    <div className="small text-[#94A3B8] fw-semibold mt-2">
                      Total Shop Credits
                    </div>

                    <div className="fw-bold text-emerald-400">
                      🪙 {formatAmount(totalPaidAmount)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body p-0">
                {paidClaims.length === 0 ? (
                  <div className="text-center py-5 px-3">
                    <i className="bi bi-receipt display-4 text-[#64748B]" />

                    <h5 className="fw-bold text-[#F8FAFC] mt-3">No Paid Claims Yet</h5>

                    <p className="text-[#94A3B8] mb-0">
                      Completed winning payouts will appear here.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* DESKTOP TABLE VIEW */}
                    <div className="hidden sm:block table-responsive">
                      <table className="table admin-table align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="ps-4">Bet ID</th>
                            <th>Computer Code</th>
                            <th>Round</th>
                            <th>Bet</th>
                            <th>Winning No.</th>
                            <th>Winning Amount</th>
                            <th>Wallet Before</th>
                            <th>Wallet After</th>
                            <th>Paid At</th>
                            <th className="text-end pe-4">Status</th>
                          </tr>
                        </thead>

                        <tbody>
                          {paidClaims.map((claim) => (
                            <tr key={claim.transaction_id}>
                              <td className="ps-4 fw-bold text-[#94A3B8]">
                                #{claim.bet_id}
                              </td>

                              <td>
                                <div className="fw-bold text-[#D4AF37]">
                                  💻 {claim.computer_code || claim.username || "N/A"}
                                </div>
                              </td>

                              <td>
                                <span className="rounded-md border border-[rgba(148,163,184,0.14)] bg-[#0D111C] px-2 py-0.5 text-xs text-[#94A3B8]">
                                  Round #{claim.round_no || "N/A"}
                                </span>
                              </td>

                              <td>
                                <div className="fw-bold text-[#D4AF37] text-uppercase">
                                  {claim.bet_type || "N/A"}
                                </div>

                                <small className="text-[#94A3B8]">
                                  Value: {claim.bet_value ?? "N/A"}
                                </small>
                              </td>

                              <td>
                                <span className="rounded-lg border border-slate-700 bg-[#0D111C] px-2.5 py-1 text-xs font-black text-[#F8FAFC]">
                                  {claim.winning_number ?? "N/A"}
                                </span>
                              </td>

                              <td className="fw-bold text-emerald-400">
                                🪙 {formatAmount(claim.win_amount)}
                              </td>

                              <td className="text-[#CBD5E1]">
                                🪙 {formatAmount(claim.shop_wallet_before)}
                              </td>

                              <td className="fw-bold text-emerald-400">
                                🪙 {formatAmount(claim.shop_wallet_after)}
                              </td>

                              <td className="small text-[#64748B]">
                                {formatDate(claim.paid_at)}
                              </td>

                              <td className="text-end pe-4">
                                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/20 px-3 py-1 text-[11px] font-bold text-emerald-400">
                                  PAID
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* MOBILE CARDS VIEW FOR PAID HISTORY */}
                    <div className="block sm:hidden p-3 space-y-3">
                      {paidClaims.map((claim) => (
                        <div
                          key={claim.transaction_id || claim.bet_id}
                          className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3 space-y-2 text-xs shadow-lg"
                        >
                          <div className="flex items-center justify-between">
                            <span className="fw-bold text-[#F8FAFC]">
                              Bet #{claim.bet_id} · Round #{claim.round_no || "N/A"}
                            </span>
                            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/20 px-2.5 py-0.5 text-[10px] font-bold text-emerald-400">
                              PAID
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-[#94A3B8]">
                            <span className="fw-bold text-[#D4AF37]">
                              💻 {claim.computer_code || claim.username || "N/A"}
                            </span>
                            <span>Win No: <strong className="text-[#F8FAFC]">{claim.winning_number ?? "N/A"}</strong></span>
                          </div>

                          <div className="bg-[#121827] p-2 rounded-lg flex items-center justify-between border border-[rgba(148,163,184,0.1)]">
                            <span className="text-[#94A3B8]">Paid Win Amount:</span>
                            <span className="fw-bold text-emerald-400">🪙 {formatAmount(claim.win_amount)}</span>
                          </div>

                          <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                            <span>Paid At: {formatDate(claim.paid_at)}</span>
                            <span>Wallet After: 🪙 {formatAmount(claim.shop_wallet_after)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      <ConfirmModal
        show={showConfirmModal}
        title="Confirm Winning Payout"
        message={`Confirm cash payout of 🪙 ${formatAmount(
          selectedBet?.win_amount
        )} to Computer 💻 ${selectedBet?.computer_code || selectedBet?.username || "N/A"} | Round #${selectedBet?.round_no || "N/A"
          } | Bet ${selectedBet?.bet_type || "N/A"} ${selectedBet?.bet_value ?? ""
          } | Winning No. ${selectedBet?.winning_number ?? "N/A"
          }. This action will mark the claim as PAID and credit the winning amount to the Shop wallet.`}
        onConfirm={handleMarkPaid}
        onClose={handleClosePayModal}
        loading={paying}
        variant="success"
        confirmText="Yes, Mark Paid"
      />
    </div>
  );
};

export default PendingWinners;
